/**
 * useObjectivesWrite — helpers d'écriture pour la collection ep:objectives.
 * Même contrat que useV1Write (campagnes/ressources) :
 *   - Structure V1 : { value: [...], updatedAt: Date.now() }
 *   - On part TOUJOURS de l'objet existant complet (spread) pour ne jamais
 *     détruire un champ non édité (ex. champs issus du sync Metricool :
 *     progress, evaluatedAt, updatedFromMetricoolAt, metricPeriodFrom/To…).
 *   - Pas de suppression définitive : l'archivage est un simple flag `archived`.
 *
 * Modèle d'un objectif (superset rétro-compatible avec les données migrées) :
 *   id, createdAt, updatedAt
 *   title, description
 *   scope        "france" | "club" | "clubs"
 *   clubs[]      ids des clubs concernés (club = 1er, conservé pour l'existant)
 *   network      réseau social (Instagram, Facebook…) — si objectif social
 *   account      compte concerné (texte libre)
 *   metricKey    clé indicateur (engagement, nouveauxAbonnes…)
 *   metricLabel  libellé lisible de l'indicateur
 *   unit         unité (%, abonnés, personnes/jour…)
 *   baseline     valeur de départ
 *   baselineDate date de référence de la valeur de départ
 *   target       valeur cible
 *   current      valeur actuelle
 *   direction    "increase" | "decrease" (sens de l'objectif)
 *   startDate, deadline
 *   owner        responsable du pilotage (id user)
 *   assignedTo[] personnes chargées des actions (ids users)
 *   priority     "basse" | "normale" | "haute" | "urgente"
 *   source       "metricool" | "manual"
 *   status       "active" | "scheduled" | "success" | "almost_success" | "failed"
 *   archived     bool
 */
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";

const EP_OBJECTIVES = "ep:objectives";

function uid() {
  return String(Date.now()) + String(Math.random()).slice(2, 8);
}

/** Valeurs par défaut d'un nouvel objectif. */
export function defaultObjective() {
  return {
    title: "",
    description: "",
    scope: "club",
    clubs: [],
    network: "",
    account: "",
    metricKey: "",
    metricLabel: "",
    unit: "",
    baseline: 0,
    baselineDate: "",
    target: 0,
    current: 0,
    direction: "increase",
    startDate: "",
    deadline: "",
    owner: "",
    assignedTo: [],
    priority: "normale",
    source: "manual",
    status: "active",
    archived: false,
  };
}

/**
 * Normalise le périmètre : garantit `clubs` (tableau) ET `club` (1er, legacy).
 * Les composants historiques lisent `o.club` ; on le maintient synchronisé.
 */
function normalizeScope(data) {
  const out = { ...data };
  if (out.scope === "france") {
    out.clubs = [];
    out.club = null;
  } else {
    const clubs = Array.isArray(out.clubs) ? out.clubs.filter(Boolean) : [];
    out.clubs = clubs;
    out.club = clubs.length ? clubs[0] : (out.club ?? null);
  }
  return out;
}

/**
 * Crée un objectif.
 * @param {Array}  objectives  tableau courant (depuis useObjectives)
 * @param {Object} formData    champs saisis
 * @param {string|number} appId  utilisateur courant (owner par défaut)
 * @returns {Promise<string>}   id du nouvel objectif
 */
export async function createObjective(objectives, formData, appId) {
  const newId = uid();
  const base = { ...defaultObjective(), ...formData };
  // current par défaut = baseline (aucune progression au départ)
  if (formData.current === undefined || formData.current === null || formData.current === "") {
    base.current = Number(base.baseline) || 0;
  }
  const newObj = normalizeScope({
    ...base,
    id: newId,
    owner: base.owner || appId,
    createdAt: new Date().toISOString(),
    updatedAt: Date.now(),
    archived: false,
  });
  const newArray = [...(objectives || []), newObj];
  await setDoc(doc(db, "appdata", EP_OBJECTIVES), {
    value: newArray,
    updatedAt: Date.now(),
  });
  return newId;
}

/**
 * Modifie un objectif existant (part de l'objet complet pour préserver les
 * champs inconnus / issus du sync Metricool).
 */
export async function updateObjective(objectives, id, patch) {
  const existing = (objectives || []).find(o => String(o.id) === String(id));
  if (!existing) throw new Error(`Objectif introuvable : ${id}`);
  const updated = normalizeScope({
    ...existing,
    ...patch,
    id: existing.id,
    updatedAt: Date.now(),
  });
  const newArray = objectives.map(o => String(o.id) === String(id) ? updated : o);
  await setDoc(doc(db, "appdata", EP_OBJECTIVES), {
    value: newArray,
    updatedAt: Date.now(),
  });
}

/**
 * Duplique un objectif. Permet de décliner un même objectif sur un autre
 * périmètre tout en conservant des cibles adaptées (via `overrides`).
 * La copie repart « propre » : pas de champs de synchro Metricool hérités.
 * @returns {Promise<string>} id de la copie
 */
export async function duplicateObjective(objectives, id, overrides = {}, appId) {
  const existing = (objectives || []).find(o => String(o.id) === String(id));
  if (!existing) throw new Error(`Objectif introuvable : ${id}`);
  const newId = uid();
  // On ne recopie PAS les champs d'évaluation/sync figés de l'original.
  const {
    id: _id, createdAt, updatedAt, progress, evaluatedAt, evaluationLabel,
    updatedFromMetricoolAt, metricPeriodFrom, metricPeriodTo,
    ...clean
  } = existing;
  const copy = normalizeScope({
    ...clean,
    ...overrides,
    id: newId,
    title: overrides.title || `${existing.title} (copie)`,
    current: overrides.current !== undefined ? overrides.current : (Number(existing.baseline) || 0),
    status: "active",
    archived: false,
    owner: overrides.owner || existing.owner || appId,
    createdAt: new Date().toISOString(),
    updatedAt: Date.now(),
  });
  const newArray = [...objectives, copy];
  await setDoc(doc(db, "appdata", EP_OBJECTIVES), {
    value: newArray,
    updatedAt: Date.now(),
  });
  return newId;
}

/** Archive / désarchive un objectif (jamais de suppression définitive). */
export async function archiveObjective(objectives, id, archived = true) {
  const existing = (objectives || []).find(o => String(o.id) === String(id));
  if (!existing) throw new Error(`Objectif introuvable : ${id}`);
  const updated = { ...existing, archived: !!archived, updatedAt: Date.now() };
  const newArray = objectives.map(o => String(o.id) === String(id) ? updated : o);
  await setDoc(doc(db, "appdata", EP_OBJECTIVES), {
    value: newArray,
    updatedAt: Date.now(),
  });
}

/* ──────────────────────────────────────────────────────────────────────────
   LIAISONS objectif ↔ actions (point 5)
   Lier un élément existant = écrire son `objectiveId`. Les blocs du détail
   filtrent déjà sur objectiveId / objective. On part toujours de l'élément
   complet (spread) pour ne rien détruire.
   ────────────────────────────────────────────────────────────────────────── */
export const LINK_COLLECTIONS = {
  campagnes:    "ep:campagnes",
  events:       "ep:events",
  publications: "ep:publications",
  tasks:        "ep:tasks",
};

/**
 * Lie (ou délie) un élément d'une collection à un objectif.
 * @param {string} collName   clé de LINK_COLLECTIONS
 * @param {Array}  items      tableau courant de la collection
 * @param {string} itemId     id de l'élément
 * @param {string|null} objectiveId  id de l'objectif, ou null pour délier
 */
export async function setItemObjective(collName, items, itemId, objectiveId) {
  const key = LINK_COLLECTIONS[collName];
  if (!key) throw new Error(`Collection inconnue : ${collName}`);
  const existing = (items || []).find(x => String(x.id) === String(itemId));
  if (!existing) throw new Error(`Élément introuvable : ${itemId}`);
  const updated = { ...existing, objectiveId: objectiveId || null };
  const newArray = items.map(x => String(x.id) === String(itemId) ? updated : x);
  await setDoc(doc(db, "appdata", key), { value: newArray, updatedAt: Date.now() });
}

/**
 * Crée une tâche directement rattachée à un objectif (ep:tasks).
 * Forme alignée sur les tâches existantes (statut « À faire », priorité FR…).
 * @returns {Promise<string>} id de la tâche créée
 */
export async function createTaskForObjective(tasks, formData, objectiveId, appId) {
  const newId = uid();
  const task = {
    id: newId,
    title: (formData.title || "").trim(),
    description: formData.description || "",
    status: "À faire",
    priority: formData.priority || "Normale",
    startDate: "",
    deadline: formData.deadline || "",
    clubs: formData.clubs || [],
    assignedTo: formData.assignedTo || [],
    objectiveId,
    owner: appId,
    createdAt: new Date().toISOString(),
    subtasks: [],
    comments: [],
    timeEntries: [],
    history: [],
  };
  const newArray = [...(tasks || []), task];
  await setDoc(doc(db, "appdata", "ep:tasks"), { value: newArray, updatedAt: Date.now() });
  return newId;
}
